// src/features/AdminDashboard/Pages/AccessCreationReportPage.tsx



import {

  useCallback,

  useEffect,


  useState,

} from 'react';



import {

  AlertCircle,

  CalendarDays,

  ChevronLeft,

  ChevronRight,

  Download,

  RefreshCw,

  Search,

  ShieldCheck,

} from 'lucide-react';



import {

  getAccessCreationReport,

  getAllAccessCreationRecords,

} from '../reports/services/publicReport.service';



import type {

  AccessCreationRecord,

} from '../reports/types/publicReport.types';



import {

  getTodayDate,

} from '../reports/utils/reportDate.utils';





// ============================================================

// CONSTANTS

// ============================================================



const PAGE_SIZE =

  10;



const EXPORT_FILE_NAME =

  'AccessCreationReport.csv';





// ============================================================

// DATE FORMAT

// ============================================================



const formatDate = (

  value:

    string,

): string => {



  if (!value) {

    return '-';

  }





  const date =

    new Date(value);





  if (

    Number.isNaN(

      date.getTime(),

    )

  ) {

    return value;

  }





  return date.toLocaleDateString(

    'en-GB',

    {

      day:

        '2-digit',



      month:

        'short',



      year:

        'numeric',

    },

  );

};





// ============================================================

// DATE TIME FORMAT

// ============================================================



const formatDateTime = (

  value:

    string,

): string => {



  if (!value) {

    return '-';

  }





  const date =

    new Date(value);





  if (

    Number.isNaN(

      date.getTime(),

    )

  ) {

    return value;

  }





  return date.toLocaleString(

    'en-GB',

    {

      day:

        '2-digit',



      month:

        'short',



      year:

        'numeric',



      hour:

        '2-digit',



      minute:

        '2-digit',



      hour12:

        true,

    },

  );

};





// ============================================================

// CSV ESCAPE

// ============================================================



const escapeCsvValue = (

  value:

    unknown,

): string => {



  const text =

    String(

      value ?? '',

    );





  return `"${text.replace(

    /"/g,

    '""',

  )}"`;

};





// ============================================================

// EXPORT CSV

// ============================================================



const downloadCsv = (

  rows:

    AccessCreationRecord[],

): void => {



  const headers = [

    'Master User Role ID',

    'Role',

    'Employee Code',

    'Employee Name',

    'Global ID',

    'Manager Name',

    'DOJ',

    'Department',

    'Access Granted',

    'Created Date',

    'Created By',

    'Last Active',

    'Is Active',

    'Sync Date',

  ];





  const csvRows =

    rows.map(

      (row) => [



        row.masterUserRoleId,



        row.role,



        row.employeeCode,



        row.employeeName,



        row.globalId,



        row.managerName,



        formatDate(

          row.doj,

        ),



        row.department,



        formatDateTime(

          row.accessGranted,

        ),



        formatDateTime(

          row.createdDate,

        ),



        row.createdBy,



        formatDateTime(

          row.lastActive,

        ),



        row.isActive

          ? 'Yes'

          : 'No',



        formatDateTime(

          row.syncDate,

        ),



      ],

    );





  const csvContent = [



    headers

      .map(

        escapeCsvValue,

      )

      .join(','),



    ...csvRows.map(

      (row) =>

        row

          .map(

            escapeCsvValue,

          )

          .join(','),

    ),



  ].join('\n');





  const blob =

    new Blob(

      [

        csvContent,

      ],

      {

        type:

          'text/csv;charset=utf-8;',

      },

    );





  const url =

    window.URL.createObjectURL(

      blob,

    );





  const link =

    document.createElement(

      'a',

    );





  link.href =

    url;



  link.download =

    EXPORT_FILE_NAME;



  link.style.display =

    'none';





  document.body.appendChild(

    link,

  );



  link.click();



  link.remove();





  window.URL.revokeObjectURL(

    url,

  );

};





// ============================================================

// PAGE

// ============================================================



const AccessCreationReportPage = () => {



  const [

    fromDate,

    setFromDate,

  ] = useState(

    '',

  );





  const [

    toDate,

    setToDate,

  ] = useState(

    '',

  );





  const [

    appliedFromDate,

    setAppliedFromDate,

  ] = useState<

    string | null

  >(

    null,

  );





  const [

    appliedToDate,

    setAppliedToDate,

  ] = useState<

    string | null

  >(

    null,

  );





  const [

    search,

    setSearch,

  ] = useState(

    '',

  );
  const [
    appliedSearch,
    setAppliedSearch,
  ] = useState<string | null>(null);






  const [

    pageNumber,

    setPageNumber,

  ] = useState(

    1,

  );





  const [

    data,

    setData,

  ] = useState<

    AccessCreationRecord[]

  >(

    [],

  );





  const [

    totalRecords,

    setTotalRecords,

  ] = useState(

    0,

  );





  const [

    totalPages,

    setTotalPages,

  ] = useState(

    0,

  );





  const [

    loading,

    setLoading,

  ] = useState(

    false,

  );





  const [

    exporting,

    setExporting,

  ] = useState(

    false,

  );





  const [

    error,

    setError,

  ] = useState<

    string | null

  >(

    null,

  );





  // ==========================================================

  // FETCH

  // ==========================================================



  const fetchData =

    useCallback(

      async () => {



        try {



          setLoading(

            true,

          );



          setError(

            null,

          );





          const response =

            await getAccessCreationReport({



              fromDate:

                appliedFromDate,



              toDate:

                appliedToDate,



              pageNumber,



              pageSize:

                PAGE_SIZE,



              search:

                appliedSearch?.trim() || null,



            });



          setData(

            Array.isArray(

              response.data,

            )

              ? response.data

              : [],

          );





          setTotalRecords(

            response.totalRecords ?? 0,

          );





          setTotalPages(

            response.totalPages ?? 0,

          );



        } catch (

          fetchError

        ) {



          console.error(

            'Access Creation API error:',

            fetchError,

          );





          setData(

            [],

          );



          setTotalRecords(

            0,

          );



          setTotalPages(

            0,

          );



          setError(

            'Unable to load Access Creation report.',

          );



        } finally {



          setLoading(

            false,

          );

        }



      },

      [

        appliedFromDate,

        appliedToDate,
        appliedSearch,
        pageNumber,
      ],

    );





  useEffect(

    () => {



      void fetchData();



    },

    [

      fetchData,

    ],

  );





  // ==========================================================

  // SEARCH

  // ==========================================================





  const applyFilters = () => {



    if (

      fromDate &&

      toDate &&

      fromDate > toDate

    ) {



      setError(

        'From Date cannot be greater than To Date.',

      );



      return;

    }





    setAppliedFromDate(

      fromDate || null,

    );



    setAppliedToDate(

      toDate || null,

    );
    setAppliedSearch(
      search.trim() || null,
    );




    setPageNumber(

      1,

    );

  };





  // ==========================================================

  // RESET

  // ==========================================================



  const resetFilters = () => {



    setFromDate(

      '',

    );



    setToDate(

      '',

    );



    setAppliedFromDate(

      null,

    );



    setAppliedToDate(

      null,

    );



    setSearch(

      '',

    );
    setAppliedSearch(null);




    setPageNumber(

      1,

    );

  };





  // ==========================================================

  // EXPORT

  // ==========================================================



  const handleExport = async () => {



    try {



      setExporting(

        true,

      );



      setError(

        null,

      );





      const rows =

        await getAllAccessCreationRecords(
          appliedFromDate,
          appliedToDate,
          appliedSearch,
        );





      downloadCsv(

        rows,

      );



    } catch (

      exportError

    ) {



      console.error(

        'Access Creation export error:',

        exportError,

      );





      setError(

        'Unable to export Access Creation report.',

      );



    } finally {



      setExporting(

        false,

      );

    }

  };





  // ==========================================================

  // TODAY

  // ==========================================================



  const today =

    getTodayDate();





  // ==========================================================

  // RENDER

  // ==========================================================



  return (

    <main className="min-h-screen bg-[#eef3f8] px-4 py-6 text-slate-900 sm:px-6 lg:px-8">



      <div className="mx-auto w-full max-w-[1440px]">



        {/* ==================================================

            HEADER

        ================================================== */}



        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">



          <div className="flex items-start gap-3">



            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">

              <ShieldCheck

                size={22}

              />

            </div>



            <div>



              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">

                Access Creation

              </h1>



              <p className="mt-1 text-sm text-slate-500">

                View newly created access and role assignments within WorkVista.

              </p>



            </div>



          </div>





          <button

            type="button"

            onClick={

              handleExport

            }

            disabled={

              exporting ||

              loading

            }

            className="

              inline-flex

              items-center

              justify-center

              gap-2

              rounded-lg

              bg-teal-600

              px-4

              py-2.5

              text-sm

              font-medium

              text-white

              shadow-sm

              transition

              hover:bg-teal-700

              disabled:cursor-not-allowed

              disabled:opacity-50

            "

          >



            {exporting ? (



              <RefreshCw

                size={16}

                className="animate-spin"

              />



            ) : (



              <Download

                size={16}

              />



            )}



            {exporting

              ? 'Exporting...'

              : 'Export CSV'}



          </button>



        </div>





        {/* ==================================================

            FILTERS

        ================================================== */}



        <section className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">



          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">



            <div>



              <label className="mb-1.5 block text-xs font-medium text-slate-600">

                From Date

              </label>



              <div className="relative">



                <CalendarDays

                  size={16}

                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"

                />



                <input

                  type="date"

                  value={

                    fromDate

                  }

                  max={

                    today

                  }

                  onChange={(event) =>

                    setFromDate(

                      event.target.value,

                    )

                  }

                  className="

                    w-full

                    rounded-lg

                    border

                    border-slate-200

                    bg-white

                    py-2.5

                    pl-10

                    pr-3

                    text-sm

                    outline-none

                    focus:border-teal-500

                    focus:ring-2

                    focus:ring-teal-100

                  "

                />



              </div>



            </div>





            <div>



              <label className="mb-1.5 block text-xs font-medium text-slate-600">

                To Date

              </label>



              <div className="relative">



                <CalendarDays

                  size={16}

                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"

                />



                <input

                  type="date"

                  value={

                    toDate

                  }

                  max={

                    today

                  }

                  onChange={(event) =>

                    setToDate(

                      event.target.value,

                    )

                  }

                  className="

                    w-full

                    rounded-lg

                    border

                    border-slate-200

                    bg-white

                    py-2.5

                    pl-10

                    pr-3

                    text-sm

                    outline-none

                    focus:border-teal-500

                    focus:ring-2

                    focus:ring-teal-100

                  "

                />



              </div>



            </div>





            <div className="flex items-end gap-2">



              <button

                type="button"

                onClick={

                  applyFilters

                }

                className="

                  inline-flex

                  flex-1

                  items-center

                  justify-center

                  gap-2

                  rounded-lg

                  bg-slate-900

                  px-4

                  py-2.5

                  text-sm

                  font-medium

                  text-white

                  hover:bg-slate-800

                "

              >

                Apply

              </button>





              <button

                type="button"

                onClick={

                  resetFilters

                }

                className="

                  inline-flex

                  items-center

                  justify-center

                  gap-2

                  rounded-lg

                  border

                  border-slate-200

                  bg-white

                  px-4

                  py-2.5

                  text-sm

                  font-medium

                  text-slate-600

                  hover:bg-slate-50

                "

              >

                <RefreshCw

                  size={15}

                />



                Reset

              </button>



            </div>



          </div>





          <div className="mt-4 relative max-w-md">



            <Search

              size={16}

              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"

            />



            <input

              type="text"

              value={

                search

              }

              onChange={(event) =>

                setSearch(

                  event.target.value,

                )

              }

              placeholder="Search employee, role, department..."

              className="

                w-full

                rounded-lg

                border

                border-slate-200

                py-2.5

                pl-10

                pr-3

                text-sm

                outline-none

                focus:border-teal-500

                focus:ring-2

                focus:ring-teal-100

              "

            />



          </div>



        </section>





        {/* ==================================================

            ERROR

        ================================================== */}



        {error && (



          <div className="mb-5 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">



            <AlertCircle

              size={17}

            />



            {error}



          </div>



        )}





        {/* ==================================================

            TABLE

        ================================================== */}



        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">



          <div className="overflow-x-auto">



            <table className="min-w-[1400px] w-full">



              <thead className="bg-slate-50">



                <tr>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Role

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Employee Code

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Employee Name

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Global ID

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Manager

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    DOJ

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Department

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Access Granted

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Created By

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Last Active

                  </th>



                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">

                    Active

                  </th>



                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">

                    Sync Date

                  </th>



                </tr>



              </thead>





              <tbody className="divide-y divide-slate-100">



                {loading ? (



                  <tr>



                    <td

                      colSpan={12}

                      className="px-4 py-12 text-center text-sm text-slate-500"

                    >

                      Loading Access Creation report...

                    </td>



                  </tr>



                ) : data.length === 0 ? (



                  <tr>



                    <td

                      colSpan={12}

                      className="px-4 py-12 text-center text-sm text-slate-500"

                    >

                      No Access Creation records found.

                    </td>



                  </tr>



                ) : (



                  data.map(

                    (row) => (



                      <tr

                        key={

                          row.masterUserRoleId

                        }

                        className="hover:bg-slate-50"

                      >



                        <td className="px-4 py-3 text-sm font-medium text-slate-700">

                          {row.role}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {row.employeeCode}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-700">

                          {row.employeeName}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {row.globalId}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {row.managerName}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {formatDate(

                            row.doj,

                          )}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {row.department}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {formatDateTime(

                            row.accessGranted,

                          )}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {row.createdBy}

                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {formatDateTime(

                            row.lastActive,

                          )}

                        </td>



                        <td className="px-4 py-3 text-center">



                          <span

                            className={

                              row.isActive

                                ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700'

                                : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500'

                            }

                          >

                            {row.isActive

                              ? 'Yes'

                              : 'No'}

                          </span>



                        </td>



                        <td className="px-4 py-3 text-sm text-slate-600">

                          {formatDateTime(

                            row.syncDate,

                          )}

                        </td>



                      </tr>



                    ),

                  )



                )}



              </tbody>



            </table>



          </div>





          {/* ==================================================

              PAGINATION

          ================================================== */}



          <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">



            <p className="text-xs text-slate-500">



              Showing page{' '}



              <span className="font-medium text-slate-700">

                {pageNumber}

              </span>



              {' '}of{' '}



              <span className="font-medium text-slate-700">

                {totalPages || 1}

              </span>



              {' '}·{' '}



              <span className="font-medium text-slate-700">

                {totalRecords}

              </span>



              {' '}records



            </p>





            <div className="flex items-center gap-2">



              <button

                type="button"

                disabled={

                  pageNumber <= 1 ||

                  loading

                }

                onClick={() =>

                  setPageNumber(

                    (previous) =>

                      Math.max(

                        1,

                        previous - 1,

                      ),

                  )

                }

                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"

              >

                <ChevronLeft

                  size={16}

                />

              </button>





              <span className="min-w-20 text-center text-xs font-medium text-slate-600">

                Page {pageNumber}

              </span>





              <button

                type="button"

                disabled={

                  totalPages === 0 ||

                  pageNumber >= totalPages ||

                  loading

                }

                onClick={() =>

                  setPageNumber(

                    (previous) =>

                      Math.min(

                        totalPages,

                        previous + 1,

                      ),

                  )

                }

                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"

              >

                <ChevronRight

                  size={16}

                />

              </button>



            </div>



          </div>



        </section>



      </div>



    </main>

  );

};





export default AccessCreationReportPage;